import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/20/solid';

// Utils
import { dateFn } from '../../utils/dateTimeFormatter';

// Images
import CalendarUpIcon from '../../images/blue-calendar-up.png';
import CalendarDownIcon from '../../images/blue-calendar-down.png';
import cartIcon from '../../images/shopping-cart-order.png';

export default function OrderConfirmation() {
  const navigate = useNavigate();
  const { orderType, orderTimeSlots, orderProvider, newOrderId, orderSummary, newSubscriptionId, orderServiceAddress } =
    useSelector((s) => s.createNewOrder);
  const pickupDate = orderType === 'oneTime' ? orderTimeSlots?.pickup_date : orderTimeSlots?.first_pickup_date;

  return (
    <div className='mx-auto max-w-lg'>
      <div className='overflow-hidden rounded-xl bg-white shadow-lg'>
        <div className='bg-green-50 px-6 py-8 text-center'>
          <div className='mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-green-100'>
            <CheckCircleIcon className='h-8 w-8 text-green-500' />
          </div>
          <h2 className='text-xl font-bold text-slate-900'>
            {orderType === 'oneTime' ? 'Order Created!' : 'Subscription Created!'}
          </h2>
          <p className='mt-1 text-sm text-slate-500'>A confirmation will be sent to your email.</p>
          <p className='mt-2 text-sm font-semibold text-slate-700'>
            {orderType === 'oneTime' ? `Order #${newOrderId}` : `Subscription #${newSubscriptionId}`}
          </p>
        </div>
        <div className='space-y-4 px-6 py-6'>
          <div className='flex gap-4'>
            <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded bg-blue-50'>
              <img src={CalendarUpIcon} className='h-4 w-4' alt='' />
            </div>
            <div>
              <p className='text-xs text-slate-500'>Pickup</p>
              <p className='text-sm font-semibold text-slate-800'>
                {dateFn(
                  'toLocal',
                  pickupDate,
                  orderTimeSlots?.pickup_start_time,
                  orderTimeSlots?.pickup_end_time,
                  orderServiceAddress?.time_zone
                )}
              </p>
            </div>
          </div>
          <div className='flex gap-4'>
            <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded bg-blue-50'>
              <img src={CalendarDownIcon} className='h-4 w-4' alt='' />
            </div>
            <div>
              <p className='text-xs text-slate-500'>Delivery</p>
              <p className='text-sm font-semibold text-slate-800'>
                {dateFn(
                  'toLocal',
                  orderTimeSlots?.dropoff_date,
                  orderTimeSlots?.dropoff_start_time,
                  orderTimeSlots?.dropoff_end_time,
                  orderServiceAddress?.time_zone
                )}
              </p>
            </div>
          </div>
          {orderSummary?.items?.length > 0 && (
            <div className='flex gap-4'>
              <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded bg-blue-50'>
                <img src={cartIcon} className='h-4 w-4' alt='' />
              </div>
              <div>
                <p className='text-xs text-slate-500'>Services</p>
                {orderSummary.items.map((s, i) => (
                  <p key={i} className='text-sm font-semibold text-slate-800'>
                    {s.name} ({s.quantity})
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <button
        type='button'
        className='bg-client-300 mt-4 w-full rounded-lg py-3 text-sm font-bold text-white shadow transition hover:opacity-90'
        onClick={() => navigate(orderType === 'oneTime' ? `/order/${newOrderId}` : '/account/')}
      >
        {orderType === 'oneTime' ? 'View Order' : 'View Subscription'}
      </button>
    </div>
  );
}
